import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { isEqual } from 'lodash-es';
import { Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

import {
  ActiveModeCommands,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  PropertiesStringValueGeneric,
  SetNumberValueCommands,
  FeaturePropertyKey,
  Feature,
  IsNumberValueProperty,
  IsActiveProperty,
  ActivateDeactivateCommands,
  DeviceIds,
  DeviceBus,
} from '@vi-iot/client';

import {
  DevicesStore,
  HemsStore,
  InstallationFeaturesStore,
  isReady,
  RichDevice,
  SmartModeAvailability,
  WallboxChargingModes,
} from '@store';

import { SUFFIX } from '../../../../shared/common/units';
import { PillType } from 'src/app/shared/components/pill/pill.model';
import { WallboxSettingsDialogComponent } from '../wallbox-settings-dialog/wallbox-settings-dialog.component';
import { IsObngWallboxPipe } from '../../installation-details-header/pipes/is-obng-wallbox.pipe/is-obng-wallbox.pipe';

@Component({
  selector: 'app-wallbox-charging-mode-overlay',
  templateUrl: './wallbox-charging-mode-overlay.component.html',
  styleUrls: ['./wallbox-charging-mode-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WallboxChargingModeOverlayComponent implements OnInit, OnDestroy {
  readonly SmartModeAvailability = SmartModeAvailability;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly SUFFIX = SUFFIX;
  readonly pillType = PillType;
  readonly wallboxChargingModes = WallboxChargingModes;
  chargingModes$: Observable<
    Feature<PropertiesStringValueGeneric<keyof typeof WallboxChargingModes>, ActiveModeCommands>
  >;
  chargingModesArray: string[];
  vehicleChargingStationAllowBatteryDischarge$: Observable<
    Feature<IsActiveProperty, ActivateDeactivateCommands>
  >;
  chargingLimit$: Observable<Feature<IsNumberValueProperty, SetNumberValueCommands>>;
  ngUnsubscribe$: Subject<void> = new Subject<void>();
  limits: number[];
  hemsGateway: string;
  isSmartChargingModeVisible = false;
  isSmartChargingModeDisabled = true;
  smartModeAvailability: string;
  smartChargingToolTipTranslationKey = '';
  private device: RichDevice;

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private hemsStore: HemsStore,
    private dialog: MatDialog,
    private devicesStore: DevicesStore
  ) {}

  ngOnInit(): void {
    this.devicesStore.selectedDevice$
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe((device) => {
        this.device = device;

        this.assignFeaturesBasingOnDeviceData();
      });

    this.hemsStore.hemsGatewaySerialIfNoError$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((gwSerial) => (this.hemsGateway = gwSerial));
  }

  private assignFeaturesBasingOnDeviceData(): void {
    this.chargingModes$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<
        PropertiesStringValueGeneric<WallboxChargingModes>,
        ActiveModeCommands
      >(FeatureNames.wallboxChargingMode, this.device.id)
      .pipe(
        filter((feature) => isReady(feature)),
        distinctUntilChanged(isEqual),
        tap((feature) => {
          this.chargingModesArray = feature?.commands?.setMode?.params?.mode?.constraints?.enum;
          this.smartModeAvailability = feature?.properties?.smartModeAvailability?.value.toLowerCase();
          this.updateSmartModeOptionVisibilityConditions(this.smartModeAvailability);
        })
      );

    this.chargingLimit$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsNumberValueProperty, SetNumberValueCommands>(
        FeatureNames.wallboxChargingLimit,
        this.device.id
      )
      .pipe(
        filter((feature) => !!feature?.commands?.setValue?.params?.value?.constraints),
        distinctUntilChanged(isEqual),
        tap((feature) => {
          const { min, max, stepping } = feature.commands.setValue.params.value.constraints;
          this.limits = [];
          for (let i = min; i <= max; i += stepping) {
            this.limits.push(i);
          }
        })
      );

    const isObng = IsObngWallboxPipe.isObngWallboxPipe(this.device);
    if (isObng) {
      this.vehicleChargingStationAllowBatteryDischarge$ = this.installationFeaturesStore
        .selectInstallationDeviceFeature<DeviceBus, never>(FeatureNames.deviceBus, this.device.id)
        .pipe(
          switchMap((feature) => {
            const busId = feature?.properties.busAddress.value;

            switch (busId) {
              case '1':
                return this.installationFeaturesStore.selectInstallationHemsFeature<
                  IsActiveProperty,
                  ActivateDeactivateCommands
                >(FeatureNames.vehicleChargingStationAllowBatteryDischargeOBNG1);
              case '2':
                return this.installationFeaturesStore.selectInstallationHemsFeature<
                  IsActiveProperty,
                  ActivateDeactivateCommands
                >(FeatureNames.vehicleChargingStationAllowBatteryDischargeOBNG2);
              case '3':
                return this.installationFeaturesStore.selectInstallationHemsFeature<
                  IsActiveProperty,
                  ActivateDeactivateCommands
                >(FeatureNames.vehicleChargingStationAllowBatteryDischargeOBNG3);
              default:
                return of(undefined);
            }
          })
        );
    } else {
      this.vehicleChargingStationAllowBatteryDischarge$ = this.installationFeaturesStore.selectInstallationHemsFeature(
        FeatureNames.vehicleChargingStationAllowBatteryDischarge
      );
    }
  }

  onVehicleChargingStation(feature: Feature<IsActiveProperty, ActivateDeactivateCommands>): void {
    this.installationFeaturesStore.updateFeature(
      feature.feature,
      {},
      FeaturePropertyKey.active,
      null,
      feature.properties.active.value ? FeatureCommands.deactivate : FeatureCommands.activate,
      this.hemsGateway,
      DeviceIds.Hems
    );
  }
  getModeLabel(mode: string): string {
    if (mode === WallboxChargingModes.GREEN) {
      return '+INSTALLATIONS.INSTALLATION.WALLBOX.GREEN_CHARGING';
    }
    if (mode === WallboxChargingModes.MIXED) {
      return '+INSTALLATIONS.INSTALLATION.WALLBOX.MIXED_CHARGING';
    }
    if (mode === WallboxChargingModes.SMART) {
      return '+INSTALLATIONS.INSTALLATION.WALLBOX.SMART_CHARGING.TITLE';
    }
  }

  onChargingModeSelect(value: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.wallboxChargingMode,
      value,
      FeaturePropertyKey.value,
      FeatureActionFields.mode,
      FeatureCommands.setMode,
      this.device.gatewaySerial,
      this.device.id
    );
  }

  updateSmartModeOptionVisibilityConditions(smartModeAvailability: string): void {
    this.isSmartChargingModeDisabled =
      smartModeAvailability === SmartModeAvailability.lockedWithPV ||
      smartModeAvailability === SmartModeAvailability.lockedWithoutPV ||
      smartModeAvailability === SmartModeAvailability.noSubscription;

    this.isSmartChargingModeVisible =
      !!smartModeAvailability?.length &&
      smartModeAvailability !== SmartModeAvailability.unavailable;

    this.smartChargingToolTipTranslationKey = this.getSmartModeTooltipTranslationKey(
      smartModeAvailability
    );
  }

  getSmartModeTooltipTranslationKey(smartModeAvailability: string): string {
    switch (smartModeAvailability) {
      case SmartModeAvailability.lockedWithPV:
        return '+INSTALLATIONS.INSTALLATION.WALLBOX.SMART_CHARGING.TOOLTIP.LOCKED_WITH_PV';

      case SmartModeAvailability.lockedWithoutPV:
        return '+INSTALLATIONS.INSTALLATION.WALLBOX.SMART_CHARGING.TOOLTIP.LOCKED_WITHOUT_PV';

      case SmartModeAvailability.noSubscription:
        return '+INSTALLATIONS.INSTALLATION.WALLBOX.SMART_CHARGING.TOOLTIP.NO_SUBSCRIPTION';

      case SmartModeAvailability.available:
      case SmartModeAvailability.unavailable:
      default:
        return '';
    }
  }

  openSettings(): void {
    this.dialog.open(WallboxSettingsDialogComponent, {
      width: '1000px',
      panelClass: 'wallbox-settings-dialog',
      data: { gatewaySerialNumber: this.hemsGateway },
    });
  }
  onChargingLimitSelect(value: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.wallboxChargingLimit,
      value,
      FeaturePropertyKey.value,
      FeatureActionFields.value,
      FeatureCommands.setValue
    );
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
